
import React, { useState, useEffect } from "react";
import { TradingSymbol } from "@/utils/mockData";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { usePriceContext } from "@/context/PriceContext";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { ArrowUp, ArrowDown, HelpCircle, Info, BarChart2, Clock, TrendingUp } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useIsMobile } from "@/hooks/use-mobile";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Sheet, SheetContent } from "@/components/ui/sheet";

interface TradingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  symbol: TradingSymbol;
  tradeType: "buy" | "sell";
}

const TradingModal: React.FC<TradingModalProps> = ({
  open,
  onOpenChange,
  symbol: initialSymbol,
  tradeType: initialTradeType
}) => {
  const { toast } = useToast();
  const { symbols } = usePriceContext();
  const isMobile = useIsMobile();
  
  // Get the most up-to-date symbol data from WebSocket
  const currentSymbol = symbols.find(s => s.name === initialSymbol.name) || initialSymbol;
  
  // State management
  const [tradeType, setTradeType] = useState<"buy" | "sell">(initialTradeType);
  const [orderType, setOrderType] = useState<"market" | "pending">("market");
  const [pendingType, setPendingType] = useState<"limit" | "stop">("limit");
  const [amount, setAmount] = useState("1");
  const [units, setUnits] = useState("1");
  const [stopLoss, setStopLoss] = useState("");
  const [takeProfit, setTakeProfit] = useState("");
  const [pendingPrice, setPendingPrice] = useState("");
  const [comment, setComment] = useState("");
  const [priceFlash, setPriceFlash] = useState<"none" | "up" | "down">("none");
  const [previousPrice, setPreviousPrice] = useState<number | null>(null);
  
  // Extract the real-time data from the WebSocket feed
  const bidPrice = currentSymbol.buyPrice;
  const askPrice = currentSymbol.sellPrice;
  const symbolName = currentSymbol.name;
  const volume = currentSymbol.volume || 0;
  const lastTradeTime = currentSymbol.lastTradeTime || Date.now();
  const spread = Math.abs(askPrice - bidPrice).toFixed(currentSymbol.digit || 2);
  const diffPercent = currentSymbol.change; // Percentage change from WebSocket
  
  // Get current prices based on trade type
  const price = tradeType === "buy" ? bidPrice : askPrice;
  
  // Auto-update the modal price when the symbol price changes
  useEffect(() => {
    if (previousPrice !== null) {
      if (price > previousPrice) {
        setPriceFlash("up");
      } else if (price < previousPrice) {
        setPriceFlash("down");
      }
      
      // Reset flash after a short delay
      const timeout = setTimeout(() => {
        setPriceFlash("none");
      }, 800);
      
      return () => clearTimeout(timeout);
    }
    
    setPreviousPrice(price);
  }, [price, previousPrice]);
  
  // Initialize pending price when order type changes to pending
  useEffect(() => {
    if (orderType === "pending" && pendingPrice === "") {
      setPendingPrice(formatPrice(price));
    }
  }, [orderType, pendingPrice, price]);
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const pendingTypeLabel = tradeType === "buy" 
      ? (pendingType === "limit" ? "Buy Limit" : "Buy Stop")
      : (pendingType === "limit" ? "Sell Limit" : "Sell Stop");
    
    const orderDetails = {
      symbol: symbolName,
      tradeType,
      orderType,
      pendingType: orderType === "pending" ? pendingType : null,
      units,
      price: orderType === "market" ? price : parseFloat(pendingPrice),
      stopLoss: stopLoss ? parseFloat(stopLoss) : null,
      takeProfit: takeProfit ? parseFloat(takeProfit) : null,
      comment
    };
    
    toast({
      title: `${tradeType === "buy" ? "Buy" : "Sell"} Order Placed`,
      description: `${orderType === "market" ? "Market" : pendingTypeLabel} order for ${units} units of ${symbolName} at ${orderType === "market" ? formatPrice(price) : pendingPrice}`,
      duration: 5000,
    });
    
    console.log("Order details:", orderDetails);
    onOpenChange(false); // Close modal after order submission
  };
  
  // Validation functions
  const validateStopLoss = () => {
    if (!stopLoss) return true;
    
    const stopLossValue = parseFloat(stopLoss);
    if (tradeType === "buy" && stopLossValue >= price) {
      return "Stop loss should be below the current price for buy orders";
    } else if (tradeType === "sell" && stopLossValue <= price) {
      return "Stop loss should be above the current price for sell orders";
    }
    
    return true;
  };
  
  const validateTakeProfit = () => {
    if (!takeProfit) return true;
    
    const takeProfitValue = parseFloat(takeProfit);
    if (tradeType === "buy" && takeProfitValue <= price) {
      return "Take profit should be above the current price for buy orders";
    } else if (tradeType === "sell" && takeProfitValue >= price) {
      return "Take profit should be below the current price for sell orders";
    }
    
    return true;
  };
  
  // Get the CSS class for the price flash effect
  const getPriceFlashClass = () => {
    if (priceFlash === "up") return "text-positive animate-pulse";
    if (priceFlash === "down") return "text-negative animate-pulse";
    return tradeType === "buy" ? "text-positive" : "text-negative";
  };
  
  // Format a price with the appropriate number of decimal places
  const formatPrice = (value: number) => {
    const decimalPlaces = currentSymbol.digit || 2;
    return value.toFixed(decimalPlaces);
  };
  
  // Format timestamp to human-readable time
  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  // For mobile devices, use the traditional modal
  if (isMobile) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md bg-background border-border shadow-lg max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center justify-between gap-2">
              <span>{symbolName}</span>
              <span className={getPriceFlashClass()}>
                {formatPrice(price)}
                {priceFlash === "up" && <ArrowUp className="inline ml-1" size={16} />}
                {priceFlash === "down" && <ArrowDown className="inline ml-1" size={16} />}
              </span>
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="flex-1 overflow-y-auto" orientation="vertical">
            <form onSubmit={handleSubmit} className="space-y-5 py-4 px-1">
              {/* Trade Type Toggle */}
              <div className="space-y-2">
                <Label>Trade Direction</Label>
                <ToggleGroup 
                  type="single" 
                  value={tradeType} 
                  onValueChange={(value) => {
                    if (value) setTradeType(value as "buy" | "sell");
                  }}
                  className="w-full grid grid-cols-2 gap-1"
                >
                  <ToggleGroupItem 
                    value="buy" 
                    className={`${tradeType === "buy" ? "bg-positive text-white" : "bg-background"} border transition-colors`}
                  >
                    Buy
                  </ToggleGroupItem>
                  <ToggleGroupItem 
                    value="sell" 
                    className={`${tradeType === "sell" ? "bg-negative text-white" : "bg-background"} border transition-colors`}
                  >
                    Sell
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
              
              {/* Order Type Radio Buttons */}
              <div className="space-y-2">
                <Label>Order Type</Label>
                <RadioGroup 
                  value={orderType} 
                  onValueChange={(value) => setOrderType(value as "market" | "pending")}
                  className="flex space-x-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="market" id="market" />
                    <Label htmlFor="market">Market Order</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="pending" id="pending" />
                    <Label htmlFor="pending">Pending Order</Label>
                  </div>
                </RadioGroup>
              </div>
              
              {/* Live Price Display */}
              <div className="rounded-md bg-muted/30 p-3 flex flex-col space-y-1">
                <div className="text-sm text-muted-foreground">Current Price</div>
                <div className={`text-lg font-semibold ${getPriceFlashClass()}`}>
                  {formatPrice(price)}
                </div>
              </div>
              
              {/* Pending Order Price - only shown for pending orders */}
              {orderType === "pending" && (
                <>
                  <div className="space-y-2 animate-fade-in">
                    <Label>Pending Order Type</Label>
                    <RadioGroup 
                      value={pendingType} 
                      onValueChange={(value) => setPendingType(value as "limit" | "stop")}
                      className="flex space-x-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="limit" id="limit" />
                        <Label htmlFor="limit">{tradeType === "buy" ? "Buy Limit" : "Sell Limit"}</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="stop" id="stop" />
                        <Label htmlFor="stop">{tradeType === "buy" ? "Buy Stop" : "Sell Stop"}</Label>
                      </div>
                    </RadioGroup>
                  </div>
                  
                  <div className="space-y-2 animate-fade-in">
                    <Label htmlFor="pendingPrice">Execution Price</Label>
                    <Input
                      id="pendingPrice"
                      value={pendingPrice}
                      onChange={(e) => setPendingPrice(e.target.value)}
                      type="number"
                      step="0.0001"
                      required
                      className="transition-all focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </>
              )}
              
              {/* Units Input */}
              <div className="space-y-2">
                <Label htmlFor="units">Units</Label>
                <Select value={units} onValueChange={setUnits}>
                  <SelectTrigger id="units" className="w-full">
                    <SelectValue placeholder="Select units" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 Unit</SelectItem>
                    <SelectItem value="2">2 Units</SelectItem>
                    <SelectItem value="5">5 Units</SelectItem>
                    <SelectItem value="10">10 Units</SelectItem>
                    <SelectItem value="20">20 Units</SelectItem>
                    <SelectItem value="50">50 Units</SelectItem>
                    <SelectItem value="100">100 Units</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Stop Loss */}
              <div className="space-y-2">
                <div className="flex items-center">
                  <Label htmlFor="stopLoss" className="mr-2">Stop Loss (optional)</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle size={16} className="text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">Stop Loss is the price at which your position will automatically close to limit potential losses.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="flex items-center">
                  <Input
                    id="stopLoss"
                    value={stopLoss}
                    onChange={(e) => setStopLoss(e.target.value)}
                    type="number"
                    step="0.0001"
                    placeholder="Optional"
                    className="transition-all focus:ring-2 focus:ring-primary"
                  />
                </div>
                {validateStopLoss() !== true && (
                  <div className="text-xs text-amber-500">{validateStopLoss()}</div>
                )}
              </div>
              
              {/* Take Profit */}
              <div className="space-y-2">
                <div className="flex items-center">
                  <Label htmlFor="takeProfit" className="mr-2">Take Profit (optional)</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle size={16} className="text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">Take Profit is the price at which your position will automatically close to secure potential gains.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="flex items-center">
                  <Input
                    id="takeProfit"
                    value={takeProfit}
                    onChange={(e) => setTakeProfit(e.target.value)}
                    type="number"
                    step="0.0001"
                    placeholder="Optional"
                    className="transition-all focus:ring-2 focus:ring-primary"
                  />
                </div>
                {validateTakeProfit() !== true && (
                  <div className="text-xs text-amber-500">{validateTakeProfit()}</div>
                )}
              </div>
              
              {/* Comment field */}
              <div className="space-y-2">
                <Label htmlFor="comment">Comment (optional)</Label>
                <Input
                  id="comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add a comment"
                  className="transition-all focus:ring-2 focus:ring-primary"
                />
              </div>
              
              {/* Order Value Preview */}
              <div className="rounded-md bg-muted/30 p-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-sm text-muted-foreground">Order Value</div>
                    <div className="font-medium">
                      {(parseFloat(units) * price).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Order Type</div>
                    <div className="font-medium capitalize">
                      {orderType} Order
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </ScrollArea>
          
          <DialogFooter className="mt-4 border-t pt-4">
            <Button 
              type="button"
              onClick={handleSubmit}
              className={`px-8 py-6 text-lg ${tradeType === "buy" 
                ? "bg-positive hover:bg-positive/90" 
                : "bg-negative hover:bg-negative/90"}`}
            >
              Open {tradeType === "buy" ? "Buy" : "Sell"} Trade
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  
  // For desktop devices, use an enhanced modal with two columns and updated WebSocket data
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl w-11/12 bg-background border-border shadow-lg max-h-[90vh] p-0 overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* Left Column: Symbol Info and Chart */}
          <div className="border-r border-border bg-muted/20 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="h-6 w-6 bg-primary/10 rounded-sm flex items-center justify-center">
                  <span className="text-xs font-semibold">{symbolName.substring(0, 2)}</span>
                </div>
                <h3 className="text-xl font-bold">{symbolName}</h3>
              </div>
              <div className="flex items-center gap-2">
                {diffPercent >= 0 ? (
                  <span className="text-positive flex items-center">
                    <ArrowUp size={12} className="mr-1" />
                    {Math.abs(diffPercent).toFixed(2)}%
                  </span>
                ) : (
                  <span className="text-negative flex items-center">
                    <ArrowDown size={12} className="mr-1" />
                    {Math.abs(diffPercent).toFixed(2)}%
                  </span>
                )}
              </div>
            </div>
            
            {/* Price information - WebSocket data */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-background p-3 rounded-md border">
                <div className="text-sm text-muted-foreground">Bid</div>
                <div className={`text-lg font-bold ${priceFlash === "up" ? "text-positive animate-pulse" : ""}`}>
                  {formatPrice(bidPrice)}
                </div>
              </div>
              <div className="bg-background p-3 rounded-md border">
                <div className="text-sm text-muted-foreground">Ask</div>
                <div className={`text-lg font-bold ${priceFlash === "down" ? "text-negative animate-pulse" : ""}`}>
                  {formatPrice(askPrice)}
                </div>
              </div>
            </div>
            
            {/* Stats grid - WebSocket data */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="p-3 rounded-md border bg-background">
                <div className="flex items-center text-sm text-muted-foreground mb-1">
                  <TrendingUp size={14} className="mr-1" />
                  Spread
                </div>
                <div className="font-semibold">{spread}</div>
              </div>
              <div className="p-3 rounded-md border bg-background">
                <div className="flex items-center text-sm text-muted-foreground mb-1">
                  <BarChart2 size={14} className="mr-1" />
                  Volume
                </div>
                <div className="font-semibold">{volume || "N/A"}</div>
              </div>
              <div className="p-3 rounded-md border bg-background">
                <div className="flex items-center text-sm text-muted-foreground mb-1">
                  <Info size={14} className="mr-1" />
                  Last Trade
                </div>
                <div className="font-semibold">
                  {formatTime(lastTradeTime)}
                </div>
              </div>
            </div>
            
            {/* Chart placeholder */}
            <div className="border rounded-md overflow-hidden bg-background">
              <AspectRatio ratio={16 / 9}>
                <div className="w-full h-full flex items-center justify-center bg-muted/30">
                  <div className="text-sm text-muted-foreground">
                    Price Chart
                  </div>
                </div>
              </AspectRatio>
            </div>
          </div>
          
          {/* Right Column: Order Form */}
          <div className="p-6 flex flex-col max-h-[80vh]">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold">Place Order</h3>
            </div>
            
            <ScrollArea className="flex-1 overflow-y-auto pr-2" orientation="vertical">
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Trade Type Toggle */}
                <div className="space-y-3">
                  <Label>Trade Direction</Label>
                  <ToggleGroup 
                    type="single" 
                    value={tradeType} 
                    onValueChange={(value) => {
                      if (value) setTradeType(value as "buy" | "sell");
                    }}
                    className="w-full grid grid-cols-2 gap-1"
                  >
                    <ToggleGroupItem 
                      value="buy" 
                      className={`${tradeType === "buy" ? "bg-positive text-white" : "bg-background"} border transition-colors`}
                    >
                      Buy
                    </ToggleGroupItem>
                    <ToggleGroupItem 
                      value="sell" 
                      className={`${tradeType === "sell" ? "bg-negative text-white" : "bg-background"} border transition-colors`}
                    >
                      Sell
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>
                
                {/* Order Type Radio Buttons */}
                <div className="space-y-3">
                  <Label>Order Type</Label>
                  <RadioGroup 
                    value={orderType} 
                    onValueChange={(value) => setOrderType(value as "market" | "pending")}
                    className="flex space-x-6"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="market" id="market-desktop" />
                      <Label htmlFor="market-desktop">Market Order</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="pending" id="pending-desktop" />
                      <Label htmlFor="pending-desktop">Pending Order</Label>
                    </div>
                  </RadioGroup>
                </div>
                
                {/* Current Price Display */}
                <div className="rounded-md bg-muted/30 p-4 flex flex-col space-y-2">
                  <div className="text-sm text-muted-foreground">Current Price</div>
                  <div className={`text-2xl font-bold ${getPriceFlashClass()}`}>
                    {formatPrice(price)}
                    {priceFlash === "up" && <ArrowUp className="inline ml-1" size={18} />}
                    {priceFlash === "down" && <ArrowDown className="inline ml-1" size={18} />}
                  </div>
                </div>
                
                {/* Pending Order Type and Price - only shown for pending orders */}
                {orderType === "pending" && (
                  <div className="space-y-4 animate-fade-in border rounded-md p-4 bg-muted/10">
                    <div className="space-y-3">
                      <Label>Pending Order Type</Label>
                      <RadioGroup 
                        value={pendingType} 
                        onValueChange={(value) => setPendingType(value as "limit" | "stop")}
                        className="flex space-x-6"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="limit" id="limit-desktop" />
                          <Label htmlFor="limit-desktop">{tradeType === "buy" ? "Buy Limit" : "Sell Limit"}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="stop" id="stop-desktop" />
                          <Label htmlFor="stop-desktop">{tradeType === "buy" ? "Buy Stop" : "Sell Stop"}</Label>
                        </div>
                      </RadioGroup>
                    </div>
                    
                    <div className="space-y-3">
                      <Label htmlFor="pendingPrice-desktop">Execution Price</Label>
                      <Input
                        id="pendingPrice-desktop"
                        value={pendingPrice}
                        onChange={(e) => setPendingPrice(e.target.value)}
                        type="number"
                        step="0.0001"
                        required
                        className="transition-all focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Units Input */}
                  <div className="space-y-3">
                    <Label htmlFor="units-desktop">Units</Label>
                    <Select value={units} onValueChange={setUnits}>
                      <SelectTrigger id="units-desktop" className="w-full">
                        <SelectValue placeholder="Select units" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 Unit</SelectItem>
                        <SelectItem value="2">2 Units</SelectItem>
                        <SelectItem value="5">5 Units</SelectItem>
                        <SelectItem value="10">10 Units</SelectItem>
                        <SelectItem value="20">20 Units</SelectItem>
                        <SelectItem value="50">50 Units</SelectItem>
                        <SelectItem value="100">100 Units</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Comment field */}
                  <div className="space-y-3">
                    <Label htmlFor="comment-desktop">Comment (optional)</Label>
                    <Input
                      id="comment-desktop"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Add a comment"
                      className="transition-all focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Stop Loss */}
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <Label htmlFor="stopLoss-desktop" className="mr-2">Stop Loss (optional)</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle size={16} className="text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <p className="max-w-xs">Stop Loss is the price at which your position will automatically close to limit potential losses.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="flex items-center">
                      <Input
                        id="stopLoss-desktop"
                        value={stopLoss}
                        onChange={(e) => setStopLoss(e.target.value)}
                        type="number"
                        step="0.0001"
                        placeholder="Optional"
                        className="transition-all focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    {validateStopLoss() !== true && (
                      <div className="text-xs text-amber-500">{validateStopLoss()}</div>
                    )}
                  </div>
                  
                  {/* Take Profit */}
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <Label htmlFor="takeProfit-desktop" className="mr-2">Take Profit (optional)</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle size={16} className="text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <p className="max-w-xs">Take Profit is the price at which your position will automatically close to secure potential gains.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="flex items-center">
                      <Input
                        id="takeProfit-desktop"
                        value={takeProfit}
                        onChange={(e) => setTakeProfit(e.target.value)}
                        type="number"
                        step="0.0001"
                        placeholder="Optional"
                        className="transition-all focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    {validateTakeProfit() !== true && (
                      <div className="text-xs text-amber-500">{validateTakeProfit()}</div>
                    )}
                  </div>
                </div>
                
                {/* Order Value Preview */}
                <div className="rounded-md bg-muted/30 p-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-muted-foreground">Order Value</div>
                      <div className="font-semibold text-lg">
                        {(parseFloat(units) * price).toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Order Type</div>
                      <div className="font-semibold text-lg capitalize">
                        {orderType === "market" ? "Market Order" : (
                          tradeType === "buy" 
                            ? (pendingType === "limit" ? "Buy Limit" : "Buy Stop") 
                            : (pendingType === "limit" ? "Sell Limit" : "Sell Stop")
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </ScrollArea>
            
            {/* Action Buttons */}
            <div className="mt-6 pt-4 border-t flex justify-end">
              <Button 
                type="button"
                onClick={handleSubmit}
                className={`px-10 py-7 text-lg ${tradeType === "buy" 
                  ? "bg-positive hover:bg-positive/90 text-white" 
                  : "bg-negative hover:bg-negative/90 text-white"}`}
              >
                {orderType === "market" 
                  ? `Open ${tradeType === "buy" ? "Buy" : "Sell"} Trade`
                  : `Place ${tradeType === "buy" 
                      ? (pendingType === "limit" ? "Buy Limit" : "Buy Stop") 
                      : (pendingType === "limit" ? "Sell Limit" : "Sell Stop")}`
                }
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TradingModal;
